import { useEffect, useState } from 'react';
import { BatchRunsTable } from '../components/BatchRunsTable';
import { LiveScansTable } from '../components/LiveScansTable';
import { abortAllLive, listLiveJobs } from '../api';
import { usePolling } from '../hooks/usePolling';
import { VIDEO_SCAN_SUBMITTED_EVENT } from '../../components/AppShell';
import { AdminAction } from '../../components/PermissionGate';

interface VideoLiveScansPageProps {
  refreshKey: number;
}

export function VideoLiveScansPage({ refreshKey }: VideoLiveScansPageProps) {
  const { data: jobs, loading, error, refresh } = usePolling(listLiveJobs, 3000, [refreshKey]);
  const [abortingAll, setAbortingAll] = useState(false);
  const [abortError, setAbortError] = useState<string | null>(null);

  const liveCount = jobs?.length ?? 0;

  const handleAbortAll = async () => {
    if (abortingAll) return;
    const ok = window.confirm(
      `Abort all ${liveCount} live scan${liveCount === 1 ? '' : 's'}?\n\n` +
        `Every job will be marked as failed. Note that fal.ai requests already in ` +
        `flight cannot be cancelled mid-call — each abort takes effect at the ` +
        `job's next checkpoint.`,
    );
    if (!ok) return;
    setAbortingAll(true);
    setAbortError(null);
    try {
      await abortAllLive();
      refresh();
    } catch (e) {
      setAbortError(e instanceof Error ? e.message : String(e));
    } finally {
      setAbortingAll(false);
    }
  };

  // Force an immediate refresh whenever the AppShell-level Generate Scan
  // modal submits, so the new job pops in instantly instead of waiting up
  // to 3 seconds for the next poll tick.
  useEffect(() => {
    const onSubmitted = () => refresh();
    window.addEventListener(VIDEO_SCAN_SUBMITTED_EVENT, onSubmitted);
    return () => window.removeEventListener(VIDEO_SCAN_SUBMITTED_EVENT, onSubmitted);
  }, [refresh]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="text-2xl font-semibold text-primary-900">Live Video Scans</h1>
          <p className="text-sm text-primary-500 mt-1">
            Pending and in-progress turntable-video jobs. Refreshes every 3 seconds.
          </p>
        </div>
        {jobs && (
          <div className="flex items-center gap-3">
            <div className="text-sm text-primary-500">
              <span className="font-semibold text-primary-900">{liveCount}</span> active
            </div>
            {liveCount > 0 && (
              <AdminAction>
                <button
                  type="button"
                  onClick={handleAbortAll}
                  disabled={abortingAll}
                  className="px-2.5 py-1 rounded-md text-xs font-medium border border-danger-200 text-danger-700 bg-white hover:bg-danger-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {abortingAll ? 'Aborting all…' : 'Abort all'}
                </button>
              </AdminAction>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          Failed to load: {error}
        </div>
      )}

      {abortError && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          Failed to abort all: {abortError}
        </div>
      )}

      <BatchRunsTable />

      {loading && !jobs ? (
        <div className="text-sm text-primary-400">Loading…</div>
      ) : (
        <LiveScansTable jobs={jobs ?? []} onMutated={refresh} />
      )}
    </div>
  );
}
